/** The quest log and the chronicle, as data.
 *
 * Two halves, and they come from different places for a reason.
 *
 * A **quest** is a rule: what a Oracle's Hut is asking for lives in that object's
 * payload, the payload is not in the observation, and whether the hero standing
 * there can pay it is a kernel question. So the host asks the kernel and hands
 * the answer over as an already-decided `JournalQuest`; the only thing done
 * here is turning the kernel's machine-readable `quest:<kind>:<args>` line
 * (`questText`, core/adventure/quests.ts) into a sentence.
 *
 * The **chronicle** is the opposite: it is built entirely out of `GameEvent`s,
 * which are plain data the shell is already handed, and the only thing it needs
 * from outside is a way to turn an id into a name. Nothing here reads state.
 *
 * Why a chronicle at all: events are the game's only narrative channel and
 * until now they were consumed by the renderer and thrown away. A player who
 * came back to a save had no way to find out that his third town had fallen on
 * day 40, and the after-action report only exists once the game is over.
 */

import type { ContentRegistry, GameEvent } from "@game/core";
import { formatNumber, humanise } from "../adventure/format.js";
import { titleCase } from "./format.js";

// ------------------------------------------------------------------ quests

export interface JournalQuest {
  /** The object's id, so the journal can centre the map on it. */
  readonly id: string;
  readonly title: string;
  /** The kernel's `questText` line, `quest:<kind>:<args>`. */
  readonly code: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** This player has already finished this one. */
  readonly done: boolean;
  /** The selected hero could pay it right now. Null when no hero is selected. */
  readonly satisfied: boolean | null;
}

/**
 * A quest line as a sentence.
 *
 * The codes are split on colons rather than parsed, because that is exactly
 * what they are: `questText` builds them by joining, and an id may not contain
 * a colon anywhere in this content set. A code this does not recognise falls
 * through to the code itself rather than to silence - an unreadable line in the
 * journal is a bug report, an empty one is a mystery.
 */
export function questSentence(code: string, content: ContentRegistry | null): string {
  const parts = code.split(":");
  if (parts[0] !== "quest") return code;
  const kind = parts[1] ?? "none";
  switch (kind) {
    case "none": return "Nothing is asked.";
    case "artifact": {
      const id = parts[2] ?? "";
      if (id === "") return "Bring an artifact.";
      const name = content?.artifacts.get(id as never)?.name ?? titleCase(id);
      // Several artifacts are named with their own article ("The Grail"), and
      // "Bring the The Grail" is the kind of seam a player reads as a bug in
      // the game rather than in the string.
      return /^(?:the|a|an)\s/i.test(name) ? `Bring ${name}.` : `Bring the ${name}.`;
    }
    case "creatures": {
      const id = parts[2] ?? "";
      const n = Number.parseInt(parts[3] ?? "0", 10);
      const def = content?.creatures.get(id as never);
      const word = n === 1 ? (def?.name ?? titleCase(id)) : (def?.namePlural ?? titleCase(id));
      return `Bring ${formatNumber(Number.isFinite(n) ? n : 0)} ${word}.`;
    }
    case "resources": {
      const res = parts[2] ?? "";
      const n = Number.parseInt(parts[3] ?? "0", 10);
      return `Bring ${formatNumber(Number.isFinite(n) ? n : 0)} ${humanise(res).toLowerCase()}.`;
    }
    case "level": {
      const n = Number.parseInt(parts[2] ?? "0", 10);
      return `Return at level ${String(Number.isFinite(n) ? n : 0)} or above.`;
    }
    case "army": {
      const n = Number.parseInt(parts[2] ?? "0", 10);
      return `Return with an army worth ${formatNumber(Number.isFinite(n) ? n : 0)}.`;
    }
    default: return code;
  }
}

/** Open quests first, then the ones already paid; stable within each group. */
export function sortQuests(quests: readonly JournalQuest[]): readonly JournalQuest[] {
  return [...quests].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

// -------------------------------------------------------------- chronicle

export interface ChronicleLine {
  readonly day: number;
  readonly week: number;
  readonly month: number;
  /** Absolute day, for sorting and for "Day 43". */
  readonly at: number;
  readonly text: string;
}

/** How the chronicle turns an id into something a player recognises. */
export interface JournalNames {
  town(id: string): string;
  hero(id: string): string;
  player(id: string): string;
}

const DAYS_PER_WEEK = 7;
const WEEKS_PER_MONTH = 4;

/**
 * One building, in words, for the chronicle and the toast.
 *
 * The toast reuses this rather than looking the names up itself, so the two
 * can never disagree about what a building - or its town - is called.
 */
export function describeBuildingBuilt(
  e: Extract<GameEvent, { type: "BuildingBuilt" }>,
  content: ContentRegistry | null,
  names: JournalNames,
): string {
  const name = content?.buildings.get(e.building)?.name ?? titleCase(e.building as string);
  return `${name} raised in ${names.town(e.town as string)}.`;
}

/**
 * The notable events, in order, with the day each happened on.
 *
 * The day is not on the event - the kernel puts it on `TurnStarted` and nowhere
 * else - so the walk carries a calendar and stamps each line as it passes. That
 * is the whole reason this is a fold rather than a filter.
 *
 * What counts as notable is a judgement and it is made here, once. The test for
 * it is whether a player scrolling back would be surprised to find the line
 * missing: a town changing hands, yes; a hero picking up ore, no. Everything
 * that fires per-stack or per-step is excluded on sight, because a chronicle
 * with four hundred lines in it is not a chronicle.
 */
export function chronicle(
  events: readonly GameEvent[], content: ContentRegistry | null, names: JournalNames,
): readonly ChronicleLine[] {
  let day = 1, week = 1, month = 1;
  const out: ChronicleLine[] = [];
  const at = (): number => (month - 1) * DAYS_PER_WEEK * WEEKS_PER_MONTH
    + (week - 1) * DAYS_PER_WEEK + day;
  const push = (text: string): void => { out.push({ day, week, month, at: at(), text }); };

  for (const e of events) {
    switch (e.type) {
      case "TurnStarted":
        day = e.day; week = e.week; month = e.month;
        break;

      case "WeekStarted":
        if (e.event !== null) push(`A ${weekEventName(e.event, content)} begins.`);
        break;

      case "MonthStarted":
        if (e.event !== null) push(`A ${weekEventName(e.event, content)} begins.`);
        break;

      case "TownCaptured":
        push(e.from === null
          ? `${names.player(e.player as string)} takes ${names.town(e.town as string)}.`
          : `${names.player(e.player as string)} takes ${names.town(e.town as string)} `
            + `from ${names.player(e.from as string)}.`);
        break;

      /**
       * Manual or governor-built alike: the build happens wherever the player
       * is not looking, and a town that grew a Capitol off-screen is
       * something they will want to be able to find afterwards. No `auto`
       * flag tells the two apart, by design - the save format carries no
       * such thing - so the book notes both the same way.
       */
      case "BuildingBuilt":
        push(describeBuildingBuilt(e, content, names));
        break;

      /**
       * Caravans are chronicle-worthy for the reason a captured town is: they
       * happen without the player watching, and a column that never arrived is
       * something they will want to be able to find afterwards. The dispatch
       * itself is not logged - that was a button the player pressed, and a
       * chronicle of one's own clicks is not a chronicle.
       */
      case "CaravanArrived": {
        const def = content?.creatures.get(e.creature);
        const what = def === undefined ? "creatures"
          : e.count === 1 ? def.name : def.namePlural;
        push(`${formatNumber(e.count)} ${what} reach ${names.town(e.town as string)}.`);
        break;
      }

      case "CaravanLost":
        push(`A caravan bound for ${names.town(e.town as string)} is lost.`);
        break;

      case "HeroDefeated":
        push(`${names.hero(e.hero as string)} falls.`);
        break;

      case "ArtifactGained": {
        const name = content?.artifacts.get(e.artifact)?.name ?? titleCase(e.artifact as string);
        push(`${names.hero(e.hero as string)} finds the ${name}.`);
        break;
      }

      case "Message": {
        const line = messageLine(e.text, e.player === null ? null : names.player(e.player as string));
        if (line !== null) push(line);
        break;
      }

      case "GameOver":
        push(e.winner === null
          ? "The war ends with nobody standing."
          : `${names.player(e.winner as string)} wins the war.`);
        break;

      default:
        break;
    }
  }
  return out;
}

/**
 * `week.creature:<id>` and `month.plague` are how the kernel names a special
 * week; the shell is the only thing that has to say them out loud.
 */
export function weekEventName(event: string, content: ContentRegistry | null): string {
  const sep = event.indexOf(":");
  const kind = sep < 0 ? event : event.slice(0, sep);
  const arg = sep < 0 ? "" : event.slice(sep + 1);
  const creature = content?.creatures.get(arg as never);
  const name = creature?.namePlural ?? titleCase(arg);
  switch (kind) {
    case "week.creature": return `Week of the ${name}`;
    case "month.creature": return `Month of the ${name}`;
    case "month.plague": return "Month of Plague";
    default: return titleCase(event);
  }
}

/**
 * The `Message` events worth a line.
 *
 * Most are machine-readable markers the shell reads for other purposes -
 * `cast:` is counted by the AI suite, `q:` marks a quest - so the chronicle
 * takes the three that are actually news and ignores the rest by default.
 */
export function messageLine(text: string, player: string | null): string | null {
  if (text.startsWith("eliminated:")) {
    const why = text.slice("eliminated:".length);
    const who = player ?? "A kingdom";
    return why === "grace_expired"
      ? `${who} is out: seven days without a town.`
      : `${who} is out: nothing left to play with.`;
  }
  if (text.startsWith("stalemate.warning:")) {
    const decide = text.slice("stalemate.warning:".length);
    return `Neither side can finish this. The war will be adjudicated on day ${decide}.`;
  }
  if (text.startsWith("stalemate.decided:")) {
    return "The war is adjudicated: the strongest kingdom takes it.";
  }
  return null;
}
